import instance from '../utils/axios';

export interface Design {
  id: number;
  design_no: string;
  name: string;
  description: string | null;
  product_type: string | null;
  production_state: 'PLANNED' | 'FABRIC_ORDERED' | 'CUTTING' | 'STITCHING' | 'EMBROIDERY' | 'PRINTING' | 'DYEING' | 'WASHING' | 'QC' | 'FINISHING' | 'PACKING' | 'DISPATCHED' | 'HOLD' | 'REWORK' | 'COMPLETE';
  track: boolean;
  top_fabric_average: number | null;
  top_fabric_width: number | null;
  top_rate: number | null;
  top_inner_average: number | null;
  top_inner_width: number | null;
  top_inner_rate: number | null;
  bottom_fabric_average: number | null;
  bottom_fabric_width: number | null;
  bottom_fabric_rate: number | null;
  bottom_inner_average: number | null;
  bottom_inner_width: number | null;
  bottom_inner_rate: number | null;
  dupatta_rate: number | null;
  dupatta_details: string | null;
  dupatta_image_ref: string | null;
  hand_work_rate: number | null;
  katha_work_rate: number | null;
  machine_work_rate: number | null;
  kali_mundi_rate: number | null;
  lace_qty: number | null;
  lace_rate: number | null;
  lace_qty2: number | null;
  lace_rate2: number | null;
  lace_qty3: number | null;
  lace_rate3: number | null;
  button_qty: number | null;
  button_rate: number | null;
  button_qty2: number | null;
  button_rate2: number | null;
  cost_price: number | null;
  bom_cost?: number | null;
  notes: string | null;
  approval_status: 'APPROVED' | 'REJECTED' | 'HOLD' | 'COMPLETE';
  design_date: string | null;
  image_url: string | null;
  image2_url: string | null;
  image3_url: string | null;
  image4_url: string | null;
  cad_file_1: string | null;
  cad_file_2: string | null;
  cad_file_3: string | null;
  cad_file_4: string | null;
  company_id: number | null;
  created_at: string;
  updated_at: string;
  design_cuttings?: any[]; // Can be defined more specifically if needed
  has_cutting?: boolean;   // summary mode: design has cutting records
  has_material?: boolean;  // summary mode: design has at least one material allocated
  has_cad?: boolean;       // summary mode: design has CAD files
  jobWorks?: { id: number; code: string; status: string; vendor: string; jobType: string; qty: number; agingDays: number }[];
  purchaseOrders?: { id: number; poNumber: string; status: string; vendor: string; totalQty: number }[];
}

export interface DesignMaterialAllocated {
  id: number;
  material_id: number;
  garment_part: string;
  is_sample: boolean;
  allocated_qty: number;
  consumed_qty: number;
  created_at: string;
  updated_at: string;
  material?: {
    material_code: string;
    material_type: string;
    material_quality: string | null;
    image_url?: string | null;
    rate: number;
  };
}

export type CreateDesignDTO = Omit<Design, 'id' | 'created_at' | 'updated_at' | 'company_id' | 'design_cuttings'> & {
  [key: string]: string | number | boolean | null | undefined;
};

export type UpdateDesignDTO = Partial<CreateDesignDTO>;

export interface DesignComment {
  id: number;
  design_no: string;
  company_id: number;
  comment: string;
  tags?: string | null;
  created_by: string;
  created_by_id?: number | null;
  parent_id?: number | null;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
  author?: { id: number; name: string; email: string } | null;
}

export interface MentionUser {
  id: number;
  name: string;
  email: string;
}

// Create a safe version of the design object for API calls
export const prepareDesignForAPI = (design: Partial<Design>): CreateDesignDTO => {
  console.log('Preparing design for API, original data:', JSON.stringify(design, null, 2));
  
  // Make a copy without the fields we don't want to send
  const { id, created_at, updated_at, company_id, design_cuttings, ...rest } = design;
  
  // Ensure required fields have proper values
  const prepared = {
    ...rest,
    design_no: design.design_no || '',
    product_type: design.product_type || 'KURTI'
  };
  
  console.log('Prepared design data:', JSON.stringify(prepared, null, 2));
  
  // Convert the design to match the required format
  return prepared as CreateDesignDTO;
};

export const designApi = {
  getAll: async () => {
    console.log('designApi.getAll: Making request to /designs');
    console.log('designApi.getAll: Token in localStorage:', localStorage.getItem('token') ? 'Present' : 'Missing');
    console.log('designApi.getAll: instance baseURL:', instance.defaults.baseURL);
    
    try {
      const response = await instance.get<Design[]>('/designs');
      console.log('designApi.getAll: Response successful', response.status);
      return response;
    } catch (error: any) {
      console.error('designApi.getAll: Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.config?.headers
      });
      throw error;
    }
  },
  
  getById: async (id: number) => {
    console.log(`designApi.getById: Making request to /designs/${id}`);
    try {
      const response = await instance.get<Design>(`/designs/${id}`);
      return response;
    } catch (error: any) {
      console.error(`designApi.getById: Error fetching design ${id}:`, error);
      throw error;
    }
  },
  
  create: async (design: CreateDesignDTO) => {
    console.log('designApi.create: Making POST request to /designs');
    try {
      const response = await instance.post<Design>('/designs', design);
      return response;
    } catch (error: any) {
      console.error('designApi.create: Error creating design:', error);
      throw error;
    }
  },
  
  update: async (id: number, design: UpdateDesignDTO) => {
    console.log(`designApi.update: Making PUT request to /designs/${id}`);
    try {
      const response = await instance.put<Design>(`/designs/${id}`, design);
      return response;
    } catch (error: any) {
      console.error(`designApi.update: Error updating design ${id}:`, error);
      throw error;
    }
  },
  
  delete: async (id: number) => {
    console.log(`designApi.delete: Making DELETE request to /designs/${id}`);
    try {
      const response = await instance.delete(`/designs/${id}`);
      return response;
    } catch (error: any) {
      console.error(`designApi.delete: Error deleting design ${id}:`, error);
      throw error;
    }
  },

  // Additional endpoints as needed
  getBySomething: async (param: string) => {
    console.log(`designApi.getBySomething: Making request to /designs/search?q=${param}`);
    try {
      const response = await instance.get<Design[]>(`/designs/search?q=${param}`);
      return response;
    } catch (error: any) {
      console.error('designApi.getBySomething: Error in search:', error);
      throw error;
    }
  },

  getAllocatedMaterials: async (designNo: string) => {
    // Returns all materials allocated to a design
    return await instance.get<DesignMaterialAllocated[]>(`/design-materials/design/${designNo}`);
  }
  ,
  updateState: async (id: number, state: Design['production_state']) => {
    return await instance.put(`/designs/${id}/state`, { state });
  },
  updateTrack: async (id: number, track: boolean) => {
    return await instance.put(`/designs/${id}/track`, { track });
  },
  listComments: async (id: number) => {
    return await instance.get<DesignComment[]>(`/designs/${id}/comments`);
  },
  addComment: async (id: number, payload: { comment: string; tags?: string; parent_id?: number }) => {
    return await instance.post<DesignComment>(`/designs/${id}/comments`, payload);
  },
  deleteComment: async (commentId: number) => {
    return await instance.delete(`/designs/comments/${commentId}`);
  },
  pinComment: async (commentId: number, is_pinned: boolean) => {
    return await instance.patch<DesignComment>(`/designs/comments/${commentId}/pin`, { is_pinned });
  },
  getCompanyUsers: async () => {
    return await instance.get<MentionUser[]>(`/designs/users/list`);
  },
  getChatConversations: async () => {
    return await instance.get(`/designs/chat/conversations`);
  },
  markChatRead: async (design_no: string) => {
    return await instance.post(`/designs/chat/mark-read`, { design_no });
  },
  trackerList: async () => {
    return await instance.get(`/designs/tracker/list`);
  },
  getApprovedWithoutJobWorks: async (params?: { summary?: number; page?: number; pageSize?: number; q?: string; withCount?: number }) => {
    return await instance.get<Design[]>('/designs/approved-without-jobworks', { params });
  }
};

// Also export a direct API call for debugging purposes
export const testDesignsEndpoint = async () => {
  console.log('Making direct fetch request to API endpoint:');
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${instance.defaults.baseURL}/designs`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    console.log('Direct fetch response:', {
      status: response.status,
      statusText: response.statusText
    });
    return response;
  } catch (error) {
    console.error('Direct fetch error:', error);
    throw error;
  }
};

export default designApi; 